/**
 * App-icon badge (PWA Badging API). On an installed web app, recent iOS
 * (16.4+, added to Home Screen, notifications allowed) and Android/desktop
 * Chrome let us put a number on the app icon. We drive it from the unread
 * count so the icon shows "what needs you" at a glance.
 *
 * Everything here feature-detects and swallows errors, so it is always safe to
 * call on unsupported browsers (it simply does nothing). Background updates
 * (when the app is closed) need a push + service worker, which lands with FCM;
 * for now we keep the badge correct whenever the app is open.
 */

type BadgeNavigator = Navigator & {
  setAppBadge?: (count?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
};

export function isBadgingSupported(): boolean {
  return typeof navigator !== 'undefined' && 'setAppBadge' in navigator;
}

export async function setAppBadge(count: number): Promise<void> {
  if (typeof navigator === 'undefined') return;
  const nav = navigator as BadgeNavigator;
  try {
    if (count > 0 && nav.setAppBadge) {
      await nav.setAppBadge(count);
    } else if (nav.clearAppBadge) {
      await nav.clearAppBadge();
    }
  } catch {
    // Unsupported or blocked — no-op.
  }
}

export async function clearAppBadge(): Promise<void> {
  if (typeof navigator === 'undefined') return;
  const nav = navigator as BadgeNavigator;
  try {
    await nav.clearAppBadge?.();
  } catch {
    // no-op
  }
}
